import { FormQuestionType } from '../form.service';
import { FormDateRangeBootstrapComponent } from './bootstrap/daterange/daterange.component';
import { IFormQuestionConvertValue } from './formquestioncontrol-base';
import { FormQuestionControlInputBase, IFormQuestionControlInputBase } from './formquestioncontrol-input-base';

/**
 * Interface to define a textbox question.
 *
 * @export
 * @interface IFormDaterangeQuestion
 * @extends {IFormQuestionControlBase<string>}
 */
export interface IFormDaterangeQuestion extends IFormQuestionControlInputBase<string> {
    datePickerPlacement?: 'top' | 'bottom' | 'left' | 'right';
    ranges?: CustomDates[];
    keyFrom?: string;
    keyTo?: string;
}

// ngx-bootstrap does not publicly export `BsCustomDates` (only via the deep, unexported path
// `ngx-bootstrap/datepicker/themes/bs/bs-custom-dates-view.component`, absent from the package's
// declared "exports" map — resolvable under TypeScript's previous `moduleResolution: "node"`,
// which reads files directly regardless of "exports", but not under Angular 20's recommended
// `"bundler"` strategy, which respects it like a real bundler would). Defined locally instead,
// matching the published shape exactly (see
// node_modules/ngx-bootstrap/datepicker/themes/bs/bs-custom-dates-view.component.d.ts) — this is
// only consumed as a structurally-typed value passed to `BsDaterangepickerConfig.ranges`, never
// imported from ngx-bootstrap itself elsewhere in this codebase.
interface BsCustomDates {
    label: string;
    value: Date | Date[];
}

export interface CustomDates extends BsCustomDates {
    key: string;
}

/**
 * Textbox question type.
 *
 * @export
 * @class FormDateQuestion
 * @extends {FormQuestionControlBase<string>}
 */
export class FormDateRangeQuestion extends FormQuestionControlInputBase<string> implements IFormQuestionConvertValue {
    override formQuestionType = FormQuestionType.Date;

    datePickerPlacement: 'top' | 'bottom' | 'left' | 'right';
    ranges: CustomDates[];
    keyFrom: string;
    keyTo: string;

    /**
     * Creates an instance of FormDateQuestion.
     * @param {IFormDateQuestion} [options={}]  Creation options.
     *
     * @memberof FormDateQuestion
     */
    constructor(options: IFormDaterangeQuestion = {}) {
        super(options);
        this.datePickerPlacement = String.isNullOrEmpty(options.datePickerPlacement) ? 'bottom' : options.datePickerPlacement;
        this.ranges = options.ranges;
        this.keyFrom = String.isNullOrEmpty(options.keyFrom) ? 'dateFrom' : options.keyFrom;
        this.keyTo = String.isNullOrEmpty(options.keyTo) ? 'dateTo' : options.keyTo;

        if (options.isInputGroup == null) {
            this.isInputGroup = true;
        }
    }

    buildFormValueFromCriteria(criteria: any, formValue: any): void {
        if (criteria[this.key + 'Preset'] != null && this.componentRef != null) {
            for (let range of (this.componentRef as FormDateRangeBootstrapComponent).dateInput.bsConfig.ranges) {
                if (range['key'] === criteria[this.key + 'Preset']) {
                    formValue[this.key] = range.value;
                }
            }
            delete formValue[this.keyFrom];
            delete formValue[this.keyTo];
        } else if (criteria[this.keyFrom] != null) {
            formValue[this.key] = [formValue[this.keyFrom], formValue[this.keyTo]];

            delete formValue[this.keyFrom];
            delete formValue[this.keyTo];
        }
    }

    buildCriteriaFromForm(formValue: any, criteria: any): void {
        if (formValue[this.key]) {
            const preset = this.getDateRangePreset(formValue[this.key]);
            if (preset != null) {
                criteria[this.key + 'Preset'] = preset;
            }
            if (this.componentRef != null) {
                criteria[this.keyFrom] = (this.componentRef as FormDateRangeBootstrapComponent).datePipe.transform(formValue[this.key][0] as Date, 'medium') as any;
                criteria[this.keyTo] = (this.componentRef as FormDateRangeBootstrapComponent).datePipe.transform(formValue[this.key][1] as Date, 'medium') as any;
            } else {
                criteria[this.keyFrom] = formValue[this.key][0].substr(0, 10) as any;
                criteria[this.keyTo] = formValue[this.key][1].substr(0, 10) as any;
            }
            delete criteria[this.key];
        }
    }

    private getDateRangePreset(dates: Date[]) {
        if (this.componentRef != null) {
            for (let range of (this.componentRef as FormDateRangeBootstrapComponent).dateInput.bsConfig.ranges) {
                if (range.value === dates) {
                    return range['key'];
                }
            }
        }
    }
}
